/**
 * The single place the app talks to the Express API.
 *
 * Auth is a same-origin httpOnly cookie, so every request sends credentials and
 * no token is ever held in JS. A non-2xx response becomes an ApiError carrying
 * the server's { error } message, which is what the UI surfaces in toasts.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The full error payload — some responses carry more than `error`,
     *  e.g. a 403 from a suspended account carries `suspended`. */
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
};

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const res = await fetch('/api' + path, {
    method: opts.method ?? 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  const data = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    const message = typeof data === 'object' && data && 'error' in data ? String(data.error) : 'Request failed';
    if (res.status === 403 && data && typeof data === 'object' && 'suspended' in data) announceSuspended();
    throw new ApiError(message, res.status, (data ?? {}) as Record<string, unknown>);
  }
  return data as T;
}

/**
 * Fired when the server rejects a request because the account was suspended
 * mid-session. AuthProvider listens and drops the session, so a suspension takes
 * effect on the user's next action rather than whenever their cookie expires.
 * An event rather than a direct call because this module has no React context.
 */
export const SUSPENDED_EVENT = 'aghosha:suspended';
const announceSuspended = () => window.dispatchEvent(new Event(SUSPENDED_EVENT));

/**
 * File upload — the one request that isn't JSON. The browser must set
 * Content-Type itself so the multipart boundary is included, hence no headers.
 */
export async function upload<T>(
  path: string,
  field: string,
  files: File[],
  /** Extra text fields sent alongside the file — e.g. the signupToken that
   *  authorises an upload made before the account exists. */
  fields: Record<string, string> = {},
): Promise<T> {
  const body = new FormData();
  for (const file of files) body.append(field, file);
  for (const [key, value] of Object.entries(fields)) body.append(key, value);

  const res = await fetch('/api' + path, { method: 'POST', credentials: 'same-origin', body });

  const data = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    const message = typeof data === 'object' && data && 'error' in data ? String(data.error) : 'Upload failed';
    throw new ApiError(message, res.status);
  }
  return data as T;
}

/** Drop empty values so we never send `?minCap=&sort=` to the API. */
export function toQuery<T extends object>(params: T): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') sp.set(key, String(value));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}
